import colorLib from "@kurkle/color";
import { useEffect, useState, useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  BarController,
  BarElement,
} from "chart.js";
import {
  Box,
  Skeleton,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
  Filler
);



const CHART_COLORS = {
  red: "rgb(255, 99, 132)",
  orange: "rgb(255, 159, 64)",
  yellow: "rgb(255, 205, 86)",
  green: "rgb(75, 192, 192)",
  blue: "rgb(54, 162, 235)",
  purple: "rgb(153, 102, 255)",
  grey: "rgb(201, 203, 207)",
};

const OVERLAY_COLORS = [
  "rgb(220, 38, 38)",
  "rgb(245, 158, 11)",
  "rgb(34, 197, 94)",
  "rgb(168, 85, 247)",
  "rgb(236, 72, 153)",
  "rgb(234, 179, 8)",
  "rgb(20, 184, 166)",
  "rgb(127, 29, 29)",
  "rgb(59, 130, 246)",
  "rgb(249, 115, 22)",
  "rgb(6, 182, 212)",
  "rgb(219, 39, 119)",
  "rgb(139, 92, 246)",
  "rgb(16, 185, 129)",
  "rgb(244, 63, 94)",
  "rgb(99, 102, 241)",
  "rgb(236, 100, 75)",
  "rgb(64, 224, 208)",
  "rgb(255, 119, 51)",
  "rgb(102, 51, 153)",
  "rgb(0, 204, 102)",
  "rgb(204, 0, 0)",
  "rgb(0, 102, 204)",
  "rgb(204, 102, 0)",
  "rgb(102, 0, 204)",
  "rgb(0, 153, 76)",
  "rgb(153, 0, 76)",
  "rgb(76, 0, 153)",
];

const conversion = (value, variable) => {
  if (typeof value !== "number" || isNaN(value)) {
    return value;
  }

  switch (variable) {
    case "TAIR":
      return (value * 9) / 5 + 32;
    case "PRCP":
      return value / 25.4;
    default:
      return value;
  }
};

const transparentize = (value, opacity = 0.5) => {
  return colorLib(value)
    .alpha(1 - opacity)
    .rgbString();
};

const WATER_YEAR_START_MONTH = 10;

const dateToMonthDay = (dateText) => {
  const [monthText = "0", dayText = "0"] = (dateText || "").split("-");
  return {
    month: Number(monthText),
    day: Number(dayText),
  };
};

const compareMonthDay = (leftDate, rightDate) => {
  const left = dateToMonthDay(leftDate);
  const right = dateToMonthDay(rightDate);
  if (left.month !== right.month) {
    return left.month - right.month;
  }
  return left.day - right.day;
};

const byCalendarDate = (records) =>
  [...records].sort((left, right) => compareMonthDay(left.date, right.date));

const toWaterYearOrder = (records) => {
  const sorted = byCalendarDate(records);
  const pivotIndex = sorted.findIndex((record) => {
    const { month } = dateToMonthDay(record.date);
    return month >= WATER_YEAR_START_MONTH;
  });

  if (pivotIndex === -1) {
    return sorted;
  }

  return [...sorted.slice(pivotIndex), ...sorted.slice(0, pivotIndex)];
};

const buildYearDayLabels = (year) => {
  const y = Number(year);
  if (!Number.isFinite(y)) return [];

  const labels = [];
  const d = new Date(Date.UTC(y, 0, 1));
  while (d.getUTCFullYear() === y) {
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(d.getUTCDate()).padStart(2, "0");
    labels.push(`${mm}-${dd}`);
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return labels;
};

const accumulateNullableArray = (arr) => {
  let sum = 0;
  return arr.map((value) => {
    if (typeof value !== "number" || Number.isNaN(value)) {
      return null;
    }
    sum += value;
    return sum;
  });
};

const chartOptions = (title, yLabel, customData = [], xLabel = "Day of the Year", hoveredDatasetIndex = null) => ({
  responsive: true,
  interaction: {
    intersect: false,
    mode: hoveredDatasetIndex !== null ? "nearest" : "index",
  },
  scales: {
    x: {
      title: {
        display: true,
        text: xLabel,
      },
    },
    y: {
      title: {
        display: true,
        text: yLabel,
      },
    },
  },
  plugins: {
    legendPositions: {},
    legend: {
      position: "top",
    },
    title: {
      display: true,
      text: title,
    },
    tooltip: {
      callbacks: {
        filter: function (tooltipItem) {
          if (hoveredDatasetIndex !== null) {
            return tooltipItem.datasetIndex === hoveredDatasetIndex;
          }
          return true;
        },
        footer: function (context) {
          if (customData?.[context[0]?.dataIndex]) {
            return customData[context[0].dataIndex];
          }
        },
      },
    },
  },
});

const ClimateChart = ({ site, year = 2026 }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isPrecipLoading, setIsPrecipLoading] = useState(false);
  const [climateData, setClimateData] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [chart2Data, setChart2Data] = useState(null);
  const [precipYearType, setPrecipYearType] = useState("calendar");
  const [selectedPrecipYears, setSelectedPrecipYears] = useState([2026]);
  const [tempPrecipYears, setTempPrecipYears] = useState([2026]);
  const [precipSeriesByYear, setPrecipSeriesByYear] = useState({});
  const [availableYears, setAvailableYears] = useState([]);
  const [hoveredPrecipDataset, setHoveredPrecipDataset] = useState(null);
  const chartRef = useRef(null);

  const precipYearOptions = availableYears.length > 0 ? availableYears : [];

  const siteLabel = typeof site === "string" ? site : site?.label || site?.value || "";
  const siteKey = typeof site === "string" ? site : site?.value || site?.label || "";

  // Fetch available years for the site
  useEffect(() => {
    if (!siteKey) {
      setAvailableYears([]);
      return;
    }

    fetch(`/api/getAvailableYears/${siteKey}?mode=${precipYearType}`)
      .then(res => res.json())
      .then(data => {
        setAvailableYears(data.years || []);
        // Reset selected precip years if needed
        if (data.years && data.years.length > 0) {
          if (!data.years.includes(selectedPrecipYears[0])) {
            setSelectedPrecipYears([data.years[0]]);
          }
        }
      })
      .catch(err => {
        console.error("Failed to fetch available years:", err);
        setAvailableYears([]);
      });
  }, [siteKey, precipYearType]);

  // Keep temp state in sync with confirmed selections
  useEffect(() => {
    setTempPrecipYears(selectedPrecipYears);
  }, [selectedPrecipYears]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const res = await fetch(`/api/climateData/${siteKey}?year=${year}`);
        const json = await res.json();
        setClimateData(json);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [siteKey, year]);

  useEffect(() => {
    const fetchPrecipSeries = async () => {
      if (!siteKey) {
        setPrecipSeriesByYear({});
        return;
      }

      if (!selectedPrecipYears.length) {
        setPrecipSeriesByYear({});
        return;
      }

      try {
        setIsPrecipLoading(true);
        const results = await Promise.all(
          selectedPrecipYears.map(async (selectedYear) => {
            const currentRes = await fetch(`/api/climateData/${siteKey}?year=${selectedYear}`);
            const currentJson = await currentRes.json();

            let previousCurrent = [];
            if (precipYearType === "water") {
              const previousRes = await fetch(`/api/climateData/${siteKey}?year=${selectedYear - 1}`);
              const previousJson = await previousRes.json();
              previousCurrent = previousJson?.current ?? [];
            }

            return [selectedYear, { current: currentJson?.current ?? [], previousCurrent }];
          })
        );

        const map = {};
        for (const [selectedYear, series] of results) {
          map[selectedYear] = series;
        }
        setPrecipSeriesByYear(map);
      } finally {
        setIsPrecipLoading(false);
      }
    };

    fetchPrecipSeries();
  }, [siteKey, selectedPrecipYears, precipYearType]);

  useEffect(() => {
    if (!climateData) return;

    const { climate = [], current = [] } = climateData || {};

    const createDataset = (label, data, options) => ({
      label,
      data,
      ...options,
    });

    // Forward-fill missing climate data to prevent gaps in lines
    const fillMissingData = (records) => {
      const byDate = new Map(records.map((record) => [record.date, record]));
      const allDates = buildYearDayLabels(year).map((date) => ({ date }));
      const orderedDates = precipYearType === "water" ? toWaterYearOrder(allDates) : byCalendarDate(allDates);
      
      let lastValue = null;
      return orderedDates.map((dateObj) => {
        const record = byDate.get(dateObj.date);
        if (record) {
          lastValue = record;
          return record;
        }
        if (lastValue) {
          // Forward-fill: use last known value for missing dates
          return { ...lastValue, date: dateObj.date };
        }
        return null;
      }).filter(Boolean);
    };

    const filledClimate = fillMissingData(climate);
    const climateByDate = new Map(filledClimate.map((record) => [record.date, record]));
    const currentByDate = new Map(current.map((record) => [record.date, record]));
    const climateDates = filledClimate.map((record) => record.date);

    const data = {
      labels: climateDates,
      datasets: [
        createDataset(
          "Maximum Maximum Air Temps",
          filledClimate
            .map((i) => conversion(i.TMAX_MAX, "TAIR"))
            .map((i) => (typeof i === "number" ? parseFloat(i).toFixed(1) : null)),
          {
            type: "line",
            borderColor: CHART_COLORS.red,
            backgroundColor: transparentize(CHART_COLORS.red),
            pointRadius: 0,
            tension: 0.2,
            fill: "1",
          }
        ),
        createDataset(
          "Average Maximum Air Temps",
          filledClimate
            .map((i) => conversion(i.TMAX_AVG, "TAIR"))
            .map((i) => (typeof i === "number" ? parseFloat(i).toFixed(1) : null)),
          {
            type: "line",
            borderColor: CHART_COLORS.green,
            backgroundColor: transparentize(CHART_COLORS.green),
            pointRadius: 0,
            tension: 0.2,
            fill: "2",
          }
        ),
        createDataset(
          "Average Minimum Air Temps",
          filledClimate
            .map((i) => conversion(i.TMIN_AVG, "TAIR"))
            .map((i) => (typeof i === "number" ? parseFloat(i).toFixed(1) : null)),
          {
            type: "line",
            borderColor: CHART_COLORS.green,
            pointRadius: 0,
            tension: 0.2,
          }
        ),
        createDataset(
          "Minimum Minimum Air Temps",
          filledClimate
            .map((i) => conversion(i.TMIN_MIN, "TAIR"))
            .map((i) => (typeof i === "number" ? parseFloat(i).toFixed(1) : null)),
          {
            type: "line",
            borderColor: CHART_COLORS.blue,
            backgroundColor: transparentize(CHART_COLORS.blue),
            pointRadius: 0,
            tension: 0.2,
            fill: "2",
          }
        ),
        createDataset(
          "Current",
          climateDates.map((date) => {
            const entry = currentByDate.get(date);
            const min = entry?.TAIR_MIN != null ? conversion(entry.TAIR_MIN, "TAIR") : null;
            const max = entry?.TAIR_MAX != null ? conversion(entry.TAIR_MAX, "TAIR") : null;
            return [
              min != null ? parseFloat(min).toFixed(1) : null,
              max != null ? parseFloat(max).toFixed(1) : null,
            ];
          }),
          {
            type: "bar",
            backgroundColor: "#000",
          }
        ),
      ],
    };

    const fullYearLabels = buildYearDayLabels(year).map((date) => ({ date }));
    const orderedClimate =
      precipYearType === "water" ? toWaterYearOrder(fullYearLabels) : byCalendarDate(fullYearLabels);
    const orderedDates = orderedClimate.map((record) => record.date);

    const lastObservedIndex = orderedDates.reduce(
      (lastIndex, date, index) =>
        currentByDate.has(date) ? index : lastIndex,
      -1
    );

    const currentDailyPrecip = orderedDates.map((date, index) => {
      const record = currentByDate.get(date);
      if (!record) {
        return index <= lastObservedIndex ? 0 : null;
      }
      return conversion(record.PRCP_SUM, "PRCP");
    });

    const climateDailyAvgPrecip = orderedDates.map((date) => {
      const record = climateByDate.get(date);
      if (!record) {
        return null;
      }
      return conversion(record.PSUM_AVG, "PRCP");
    });

    const currentAccumulated = accumulateNullableArray(currentDailyPrecip).map((value) =>
      value == null ? null : parseFloat(value).toFixed(2)
    );
    const climateAccumulated = accumulateNullableArray(climateDailyAvgPrecip).map((value) =>
      value == null ? null : parseFloat(value).toFixed(2)
    );
    const surplusDeficitAccumulated = currentAccumulated.map((value, index) => {
      const averageValue = climateAccumulated[index];
      if (value == null || averageValue == null) {
        return null;
      }

      return (Number(value) - Number(averageValue)).toFixed(2);
    });

    const lastCurrentIndex = currentDailyPrecip.reduce(
      (lastIndex, value, index) => (value == null ? lastIndex : index),
      -1
    );

    // Only show red background if exactly one year is selected
    const shouldShowRedBackground = selectedPrecipYears.length === 1;

    const selectedYearsSorted = [...selectedPrecipYears].sort((a, b) => a - b);
    const overlayLastObservedByYear = {};
    let firstSelectedYearAccumulated = null;

    const overlayDatasets = selectedYearsSorted.map((selectedYear, idx) => {
      const series = precipSeriesByYear[selectedYear] || { current: [], previousCurrent: [] };
      const currentByDateForYear = new Map((series.current || []).map((record) => [record.date, record]));
      const previousByDate = new Map((series.previousCurrent || []).map((record) => [record.date, record]));

      const getRecordForDate = (date) => {
        const month = Number((date || "").slice(0, 2));
        if (precipYearType === "water" && month >= WATER_YEAR_START_MONTH) {
          return previousByDate.get(date);
        }
        return currentByDateForYear.get(date);
      };

      const availableIndices = orderedDates
        .map((date, index) => (getRecordForDate(date) ? index : -1))
        .filter((index) => index >= 0);
      const lastObserved = availableIndices.length ? Math.max(...availableIndices) : -1;
      overlayLastObservedByYear[selectedYear] = lastObserved;

      const daily = orderedDates.map((date, index) => {
        const record = getRecordForDate(date);
        if (!record) {
          return index <= lastObserved ? 0 : null;
        }
        return conversion(record.PRCP_SUM, "PRCP");
      });

      const accumulated = accumulateNullableArray(daily).map((value) =>
        value == null ? null : parseFloat(value).toFixed(2)
      );

      if (idx === 0) {
        firstSelectedYearAccumulated = accumulated;
      }

      const color = selectedYearsSorted.length > 1
        ? OVERLAY_COLORS[idx % OVERLAY_COLORS.length]
        : [CHART_COLORS.green, CHART_COLORS.blue, CHART_COLORS.red, CHART_COLORS.orange, CHART_COLORS.purple, CHART_COLORS.grey][idx % 6];

      const dataset = createDataset(`${selectedYear} Accumulated Precipitation`, accumulated, {
        type: "line",
        borderColor: color,
        backgroundColor: transparentize(color),
        pointRadius: 0,
        tension: 0.2,
        fill: selectedYearsSorted.length === 1 ? { target: "origin" } : false,
      });

      if (selectedYearsSorted.length === 1 && idx === 0) {
        const isCurrentYear = Number(selectedYear) === new Date().getFullYear();
        const fillCutoffIndex = overlayLastObservedByYear[selectedYear];

        dataset.backgroundColor = isCurrentYear
          ? (ctx) => {
              const index = ctx.index;
              return index <= fillCutoffIndex
                ? transparentize(CHART_COLORS.green)
                : "rgba(0, 0, 0, 0)";
            }
          : transparentize(CHART_COLORS.green);

        dataset.segment = isCurrentYear
          ? {
              backgroundColor: (ctx) => {
                const index = ctx.p0DataIndex;
                return index <= fillCutoffIndex
                  ? transparentize(CHART_COLORS.green)
                  : "rgba(0, 0, 0, 0)";
              },
            }
          : undefined;
      }

      return dataset;
    });

    const selectedYearSurplusDeficit = firstSelectedYearAccumulated
      ? firstSelectedYearAccumulated.map((value, index) => {
          const averageValue = climateAccumulated[index];
          if (value == null || averageValue == null) return null;
          return (Number(value) - Number(averageValue)).toFixed(2);
        })
      : surplusDeficitAccumulated;

    const selectedSingleYear = selectedYearsSorted[0];
    const selectedSingleYearCutoff = overlayLastObservedByYear[selectedSingleYear] ?? -1;
    const shouldCutoffAverageFill =
      selectedYearsSorted.length === 1 &&
      Number(selectedSingleYear) === new Date().getFullYear() &&
      selectedSingleYearCutoff >= 0;

    const data2 = {
      labels: orderedDates,
      datasets: [
        ...overlayDatasets,
        createDataset(
          "Average Accumulated Precipitation",
          climateAccumulated,
          {
            type: "line",
            borderColor: CHART_COLORS.green,
            pointRadius: 0,
            tension: 0.2,
            fill: selectedYearsSorted.length === 1 ? { target: "origin" } : false,
            backgroundColor:
              selectedYearsSorted.length === 1
                ? (shouldCutoffAverageFill
                    ? (ctx) => {
                        const index = ctx.index;
                        return index < selectedSingleYearCutoff
                          ? transparentize(CHART_COLORS.red)
                          : "rgba(0, 0, 0, 0)";
                      }
                    : transparentize(CHART_COLORS.red))
                : "rgba(0, 0, 0, 0)",
            segment:
              selectedYearsSorted.length === 1
                ? shouldCutoffAverageFill
                  ? {
                      backgroundColor: (ctx) => {
                        const index = ctx.p0DataIndex;
                        return index < selectedSingleYearCutoff
                          ? transparentize(CHART_COLORS.red)
                          : "rgba(0, 0, 0, 0)";
                      },
                    }
                  : {
                      backgroundColor: transparentize(CHART_COLORS.red),
                    }
                : undefined,
          }
        ),
        ...(selectedYearsSorted.length === 1
          ? [
              createDataset(
                "Accumulated Surplus/Deficit Precipitation",
                selectedYearSurplusDeficit,
                {
                  type: "line",
                  borderColor: CHART_COLORS.orange,
                  pointRadius: 0,
                  tension: 0.2,
                  fill: false,
                }
              ),
            ]
          : []),
      ],
    };

    // Apply hover effects to datasets if needed
    if (hoveredPrecipDataset !== null) {
      data2.datasets.forEach((dataset, idx) => {
        if (idx !== hoveredPrecipDataset) {
          // Dim non-hovered datasets
          if (dataset.borderColor && typeof dataset.borderColor === "string" && dataset.borderColor.startsWith("rgb(")) {
            dataset.borderColor = dataset.borderColor.replace("rgb(", "rgba(").replace(")", ", 0.15)");
          }
          if (dataset.backgroundColor && typeof dataset.backgroundColor === "string" && dataset.backgroundColor.startsWith("rgb(")) {
            dataset.backgroundColor = dataset.backgroundColor.replace("rgb(", "rgba(").replace(")", ", 0.08)");
          }
        }
      });
    }

    setChartData(data);
    setChart2Data(data2);
  }, [climateData, precipYearType, precipSeriesByYear, selectedPrecipYears, hoveredPrecipDataset]);

  if (isLoading || isPrecipLoading || !chartData) {
    return (
      <>
        <Box sx={{ display: "flex", pt: 1, justifyContent: "flex-end" }}>
          <Skeleton height={25} width={300} sx={{ transform: "none" }} />
        </Box>
        <Skeleton height={500} sx={{ my: 1, transform: "none" }} />
        <Skeleton height={500} sx={{ my: 1, transform: "none" }} />
      </>
    );
  }

  // Check if the selected year has sufficient data
  const currentYear = new Date().getFullYear();
  const isCurrentYear = year === currentYear;
  const currentDataDays = climateData?.current?.length || 0;
  const minDaysRequired = isCurrentYear ? 1 : 330;

  if (currentDataDays < minDaysRequired) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="body1" color="textSecondary">
          {isCurrentYear
            ? "No data available for the current year yet."
            : `Year ${year} does not have enough data to display. (${currentDataDays} days found, ${minDaysRequired} required)`}
        </Typography>
      </Box>
    );
  }

  const precipFooterData =
    selectedPrecipYears.length === 1 && chart2Data?.labels?.length
      ? chart2Data.labels.map(() =>
          precipYearType === "water"
            ? `Day of the ${selectedPrecipYears[0]} Water Year`
            : `Day of the ${selectedPrecipYears[0]} Year`
        )
      : [];

  return (
    <>
      <Box sx={{ display: "flex", pt: 1, justifyContent: "flex-end" }}>
        <Typography>
          Last Update:{" "}
          {(() => {
            const lastDate = climateData?.current && climateData.current.length
              ? climateData.current.at(-1).date
              : null;
            return lastDate
              ? new Date(`${year}-${lastDate}`).toISOString()
              : "N/A";
          })()}
        </Typography>
      </Box>
      <Line
        data={chartData}
        options={chartOptions(
          `${siteLabel} Climate Graph`,
          "Degrees F",
          (climateData?.climate ?? []).map((i) => `MAX Year ${i?.TMAX_YEAR ?? ""}, MIN Year ${i?.TMIN_YEAR ?? ""}`)
        )}
      />
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 3, mb: 1 }}>
        <FormControl sx={{ minWidth: { xs: "100%", md: 260 } }}>
          <InputLabel id="precip-year-type-label">Precip Year Type</InputLabel>
          <Select
            labelId="precip-year-type-label"
            id="precip-year-type"
            value={precipYearType}
            label="Precip Year Type"
            onChange={(event) => setPrecipYearType(event.target.value)}
          >
            <MenuItem value="calendar">Calendar Year (Jan 1 to Dec 31)</MenuItem>
            <MenuItem value="water">Water Year (Oct 1 to Sep 30)</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: { xs: "100%", md: 260 } }}>
          <InputLabel id="precip-years-label">Precip Years</InputLabel>
          <Select
            labelId="precip-years-label"
            id="precip-years"
            multiple
            value={tempPrecipYears}
            input={<OutlinedInput label="Precip Years" />}
            label="Precip Years"
            onChange={(event) => {
              const values = Array.isArray(event.target.value) ? event.target.value : [];
              const numericValues = values.filter((v) => typeof v === "number" || !isNaN(Number(v)));
              const next = numericValues.map((v) => Number(v)).sort((a, b) => a - b);
              setTempPrecipYears(next);
            }}
            onClose={() => setSelectedPrecipYears(tempPrecipYears)}
            renderValue={(selected) => {
              if (!selected.length) return "(None selected)";
              if (selected.length <= 2) return selected.join(", ");
              return `${selected.slice(0, 2).join(", ")}... (+${selected.length - 2} more)`;
            }}
          >
            <MenuItem
              onMouseDown={(e) => {
                e.preventDefault();
                setTempPrecipYears([...precipYearOptions]);
              }}
            >
              <ListItemText primary="Select All" />
            </MenuItem>
            <MenuItem
              onMouseDown={(e) => {
                e.preventDefault();
                setTempPrecipYears([]);
              }}
            >
              <ListItemText primary="Clear" />
            </MenuItem>
            {precipYearOptions.map((y) => (
              <MenuItem key={y} value={y}>
                <Checkbox checked={tempPrecipYears.indexOf(y) > -1} />
                <ListItemText primary={y} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      <Box
        onMouseMove={(event) => {
          if (!chartRef.current) return;

          const chartInstance = chartRef.current.chartInstance || chartRef.current;
          if (!chartInstance || !chartInstance.canvas) return;

          const rect = chartInstance.canvas.getBoundingClientRect();
          const mouseX = event.clientX - rect.left;
          const mouseY = event.clientY - rect.top;

          const nativeEvent = event.nativeEvent || event;

          const activeElements = chartInstance.getElementsAtEventForMode(
            nativeEvent,
            "nearest",
            { intersect: true },
            true
          );

          if (activeElements?.length) {
            setHoveredPrecipDataset(activeElements[0].datasetIndex);
            return;
          }

          const legend = chartInstance.legend;
          const hitBoxes = legend?.legendHitBoxes || [];
          const legendItems = legend?.legendItems || [];
          const boxWidth = chartInstance.options?.plugins?.legend?.labels?.boxWidth ?? 40;
          const boxHeight = chartInstance.options?.plugins?.legend?.labels?.boxHeight ?? 12;
          const boxPadding = chartInstance.options?.plugins?.legend?.labels?.padding ?? 10;

          for (let index = 0; index < hitBoxes.length; index += 1) {
            const hitBox = hitBoxes[index];
            const item = legendItems[index];
            if (!hitBox || !item) continue;

            const boxX = hitBox.left;
            const boxY = hitBox.top + Math.max(0, (hitBox.height - boxHeight) / 2);
            const boxRight = boxX + boxWidth + boxPadding / 2;
            const boxBottom = boxY + boxHeight;

            if (mouseX >= boxX && mouseX <= boxRight && mouseY >= boxY && mouseY <= boxBottom) {
              setHoveredPrecipDataset(index);
              return;
            }
          }

          setHoveredPrecipDataset(null);
        }}
        onMouseLeave={() => setHoveredPrecipDataset(null)}
      >
        <Line
          ref={chartRef}
          data={chart2Data}
          options={chartOptions(
            `${siteLabel} ${precipYearType === "water" ? "Water Year" : "Calendar Year"} Precipitation Graph`,
            "Inches",
            [],
            selectedPrecipYears.length === 1
              ? precipYearType === "water"
                ? `Day of the ${selectedPrecipYears[0]} Water Year`
                : `Day of the ${selectedPrecipYears[0]} Year`
              : precipYearType === "water"
                ? "Day of the Water Year"
                : "Day of the Year",
            hoveredPrecipDataset
          )}
        />
      </Box>
    </>
  );
};

export default ClimateChart;
